import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useBranding } from "@/context/BrandingContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const { companyName } = useBranding();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!value) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/auth/forgot-password`,
        { email: value },
        { withCredentials: true }
      );
      setSent(true);
      toast.success("Återställningsmail skickat");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : "Vi kunde inte skicka mailet. Försök igen om en stund.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="forgot-password-page"
      className="min-h-screen flex items-center justify-center bg-cover bg-center relative"
      style={{
        backgroundImage:
          'url("https://images.unsplash.com/photo-1591638212063-c2f69701a931?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHx3b29kd29ya2luZyUyMHN0YWlycyUyMG1vZGVybiUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODAxNDQzMDh8MA&ixlib=rb-4.1.0&q=85")',
      }}
    >
      <div className="absolute inset-0 bg-black/55" />
      <div className="relative z-10 bg-surface p-10 md:p-12 rounded-2xl max-w-md w-full mx-4 border border-app">
        <div className="mb-2 text-xs tracking-[0.2em] uppercase text-text-muted">
          {companyName}
        </div>
        <h1 className="font-heading text-3xl md:text-4xl tracking-tight text-text-primary leading-[1.1]">
          Glömt lösenord?
        </h1>
        <p className="mt-4 text-text-muted text-sm">
          Ange e-postadressen som är kopplad till ditt konto. Vi skickar en
          återställningslänk som är giltig i 1 timme.
        </p>

        {sent ? (
          <div
            data-testid="forgot-password-sent-state"
            className="mt-8 space-y-4"
          >
            <div className="rounded-lg border border-brand/40 bg-brand/10 p-4 text-sm text-text-primary">
              Ett återställningsmail har skickats till{" "}
              <strong>{email}</strong>. Kontrollera din inkorg (och skräpposten
              om det dröjer).
            </div>
            <Link
              to="/login"
              data-testid="forgot-password-back-to-login"
              className="tk-btn-secondary w-full block text-center py-3"
            >
              Tillbaka till inloggning
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-8 space-y-4">
            <div>
              <label
                className="block text-xs text-text-muted mb-1.5"
                htmlFor="email"
              >
                E-post
              </label>
              <input
                id="email"
                type="email"
                required
                data-testid="forgot-password-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                autoFocus
                className="tk-input w-full"
                placeholder="namn@foretag.se"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="forgot-password-submit-button"
              className="tk-btn-primary w-full mt-2"
            >
              {submitting ? "Skickar…" : "Skicka återställningslänk"}
            </button>
          </form>
        )}

        <p className="mt-6 text-[11px] text-text-muted">
          Kom du på lösenordet?{" "}
          <Link
            to="/login"
            className="text-brand hover:underline"
            data-testid="forgot-password-login-link"
          >
            Tillbaka till inloggning
          </Link>
        </p>
      </div>
    </div>
  );
}
